import {ChangeEvent, Dispatch, SetStateAction, useCallback, useState} from "react";
import {extractEvent} from "@/lib/utils";

/**
 * React hook that persists a value of type `T` in `localStorage` under `itemName`.
 *
 * On mount the stored JSON is parsed and returned as the initial state; on every update the
 * new value is JSON-serialised and written back. Falls back to `init` when storage is empty
 * or contains invalid JSON.
 *
 * @param itemName - The `localStorage` key. Pass `undefined` to disable persistence (pure in-memory state).
 * @param init     - Initial value or factory function used when storage is empty.
 */
export const useLocalState = <T>(
    itemName: string | undefined,
    init?: T | (() => T)
): [T | undefined, Dispatch<SetStateAction<T>>, (e: ChangeEvent) => void] => {
    const initFnc = useCallback((): T | undefined => {
        const val = itemName == null ? null : localStorage.getItem(itemName);
        if (val != null) {
            try {
                const data = JSON.parse(val) as T | null;
                if (data != null) {
                    return data;
                }
            } catch (e) {
                console.error("Failed to parse local state", val, e);
            }
        }
        // Cast at the system boundary: `typeof init === "function"` guarantees `init` is a
        // factory `() => T` here. TypeScript cannot narrow this when `T` is a primitive (e.g.
        // `boolean`), so a boundary cast is the project-sanctioned alternative to `@ts-ignore`.
        if (typeof init === "function") {
            return (init as () => T)();
        }
        return init;
    }, [init, itemName]);

    const [state, setStateInt] = useState<T>(initFnc as () => T);

    const setState = useCallback(
        (val: T | ((old: T) => T)) => {
            setStateInt((old: T) => {
                // Cast at the system boundary: `typeof val === "function"` narrows `val` to the
                // updater function, but TypeScript cannot infer this when `T` may be a primitive.
                const next: T = typeof val === "function" ? (val as (old: T) => T)(old) : val;
                if (itemName) {
                    const json = JSON.stringify(next);
                    localStorage.setItem(itemName, json);
                }
                return next;
            });
        },
        [setStateInt, itemName]
    );

    const onChange = useCallback(
        (e: ChangeEvent) => {
            const nv = extractEvent(e);
            if (nv) {
                setState((old) => ({ ...(old as object), [nv[0]]: nv[1] } as T));
            } else {
                console.error("onChange called without name!");
            }
        },
        [setState]
    );

    return [state, setState as Dispatch<SetStateAction<T>>, onChange];
};
